// One row in the take-review queue. Three actions: approve, pickup, archive.
// Click anywhere on the row body opens the detail modal (parent owns).
//
// The queue endpoint returns a slim envelope (`ReviewQueueItem`) — full
// take detail (waveform, QC metrics, audio URL) is fetched on click via
// `useReviewTake`. So this row renders the slim shape; waveform + QC chips
// only show when the parent has hydrated the rich `TakeRow` separately.

import { Button } from '@ohear/ui'
import type { ReviewQueueItem } from '../../lib/api/producer-takes'
import type { TakeQcMetrics } from '../../lib/api/producer-types'
import { QcMetrics } from './QcMetrics'
import { WaveformThumb } from './WaveformThumb'

export interface TakeReviewRowProps {
  take: ReviewQueueItem
  /** Optional rich detail (waveform peaks + QC metrics) once hydrated. */
  detail?: {
    waveform_peaks: number[] | null
    qc_metrics: TakeQcMetrics
  }
  selected: boolean
  onSelect: (next: boolean) => void
  onOpen: () => void
  onApprove: () => void
  onPickup: () => void
  onArchive: () => void
  busy?: boolean
}

export function TakeReviewRow({
  take,
  detail,
  selected,
  onSelect,
  onOpen,
  onApprove,
  onPickup,
  onArchive,
  busy,
}: TakeReviewRowProps) {
  return (
    <tr className="group align-middle transition-colors hover:bg-[var(--bg-3)]">
      <td className="px-2 py-2 border-t" style={{ borderColor: 'var(--border)' }}>
        <input
          type="checkbox"
          aria-label={`Select take ${take.id}`}
          checked={selected}
          onChange={(e) => onSelect(e.target.checked)}
        />
      </td>
      <td
        className="px-2 py-2 text-[12px] font-mono text-[var(--text-2)] border-t"
        style={{ borderColor: 'var(--border)' }}
      >
        {take.character_code}
      </td>
      <td
        className="px-2 py-2 text-[12px] font-mono text-[var(--text-2)] border-t"
        style={{ borderColor: 'var(--border)' }}
      >
        {take.cue_number ?? '—'}
      </td>
      <td
        className="cursor-pointer px-2 py-2 text-[13px] text-[var(--text)] border-t"
        style={{ borderColor: 'var(--border)' }}
        onClick={onOpen}
      >
        <span className="line-clamp-2">
          {take.line_text ?? <em className="text-[var(--text-3)]">no text</em>}
        </span>
      </td>
      <td className="px-2 py-2 border-t" style={{ borderColor: 'var(--border)' }}>
        <WaveformThumb peaks={detail?.waveform_peaks ?? null} />
      </td>
      <td className="px-2 py-2 border-t" style={{ borderColor: 'var(--border)' }}>
        {detail ? (
          <QcMetrics metrics={detail.qc_metrics} />
        ) : (
          <span className="text-[11px] text-[var(--text-3)]">—</span>
        )}
      </td>
      <td className="px-2 py-2 border-t" style={{ borderColor: 'var(--border)' }}>
        <div className="flex flex-wrap gap-1">
          <Button size="sm" onClick={onApprove} disabled={busy}>
            Approve
          </Button>
          <Button size="sm" variant="ghost" onClick={onPickup} disabled={busy}>
            Pickup
          </Button>
          <Button size="sm" variant="ghost" onClick={onArchive} disabled={busy}>
            Archive
          </Button>
        </div>
      </td>
    </tr>
  )
}
